import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatNativeDateModule } from '@angular/material/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ComplianceFinding } from '../models/quality.models';
import { LoadingStateComponent } from './loading-state.component';
import { EmptyStateComponent } from './empty-state.component';
import { ErrorStateComponent } from './error-state.component';
import { ComplianceBadgeComponent } from './compliance-badge.component';
import { ReviewStatusControlComponent } from './review-status-control.component';

@Component({
  selector: 'app-weekly-alerts-widget',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    MatNativeDateModule,
    MatTooltipModule,
    LoadingStateComponent,
    EmptyStateComponent,
    ErrorStateComponent,
    ComplianceBadgeComponent,
    ReviewStatusControlComponent,
  ],
  templateUrl: './weekly-alerts-widget.component.html',
  styleUrls: ['./weekly-alerts-widget.component.scss'],
})
export class WeeklyAlertsWidgetComponent implements OnInit {
  @Input() title = '';
  @Input() findings: ComplianceFinding[] = [];
  @Input() loading = false;
  @Input() error = '';
  @Input() updatingReviewKeys: ReadonlySet<string> = new Set();
  @Output() weekSelected = new EventEmitter<number>();
  @Output() dateRangeChange = new EventEmitter<{
    startDate: string;
    endDate: string;
  }>();
  @Output() reviewStatusChange = new EventEmitter<ComplianceFinding>();

  selectedWeek = 0;
  selectedFinding: ComplianceFinding | null = null;
  showResults = true;

  startDate: Date | null = null;
  endDate: Date | null = null;

  ngOnInit(): void {
    this.syncDatesWithWeek(0);
  }

  toggleReviewStatus(item: ComplianceFinding): void {
    this.reviewStatusChange.emit(item);
  }

  isUpdatingReview(item: ComplianceFinding): boolean {
    return this.updatingReviewKeys.has(`${item.pk}#${item.sk}`);
  }

  selectWeek(weeksAgo: number): void {
    this.selectedWeek = weeksAgo;
    this.selectedFinding = null;
    this.syncDatesWithWeek(weeksAgo);
    this.weekSelected.emit(weeksAgo);
    const boundary = this.calculateWeekBoundary(weeksAgo);
    this.dateRangeChange.emit({
      startDate: boundary.startIso,
      endDate: boundary.endIso,
    });
  }

  onCustomDateChange(): void {
    this.selectedWeek = -1;
  }

  applyCustomRange(): void {
    if (!this.startDate || !this.endDate) return;
    this.selectedWeek = -1;
    this.selectedFinding = null;

    const start = new Date(this.startDate);
    const startIso = new Date(
      Date.UTC(
        start.getFullYear(),
        start.getMonth(),
        start.getDate(),
        0,
        0,
        0,
        0
      )
    ).toISOString();

    const end = new Date(this.endDate);
    const endIso = new Date(
      Date.UTC(
        end.getFullYear(),
        end.getMonth(),
        end.getDate(),
        23,
        59,
        59,
        999
      )
    ).toISOString();

    this.dateRangeChange.emit({
      startDate: startIso,
      endDate: endIso,
    });
  }

  toggleShowResults(): void {
    this.showResults = !this.showResults;
  }

  hideCompleted = false;

  toggleHideCompleted(): void {
    this.hideCompleted = !this.hideCompleted;
  }

  customerIdSearch = '';

  clearCustomerIdSearch(): void {
    this.customerIdSearch = '';
  }

  get visibleFindings(): ComplianceFinding[] {
    let result = this.findings;
    if (this.hideCompleted) {
      result = result.filter((item) => item.reviewStatus !== 'review-complete');
    }
    const search = this.customerIdSearch.trim().toLowerCase();
    if (search) {
      result = result.filter((item) =>
        (item.customerId || '').toLowerCase().includes(search)
      );
    }
    return result;
  }

  get completedCount(): number {
    return this.findings.filter(
      (item) => item.reviewStatus === 'review-complete'
    ).length;
  }

  selectFinding(finding: ComplianceFinding): void {
    this.selectedFinding = this.selectedFinding === finding ? null : finding;
  }

  get displayStartDate(): string {
    if (this.startDate) {
      return this.formatDate(this.startDate);
    }
    return this.calculateWeekBoundary(
      this.selectedWeek >= 0 ? this.selectedWeek : 0
    ).start;
  }

  get displayEndDate(): string {
    if (this.endDate) {
      return this.formatDate(this.endDate);
    }
    return this.calculateWeekBoundary(
      this.selectedWeek >= 0 ? this.selectedWeek : 0
    ).end;
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private syncDatesWithWeek(weeksAgo: number): void {
    const boundary = this.calculateWeekBoundary(weeksAgo);
    this.startDate = boundary.startDateObj;
    this.endDate = boundary.endDateObj;
  }

  /**
   * Calculates a cumulative period window ending today.
   * `weeksAgo` here means "number of trailing weeks included" (0 = last 7 days,
   * 1 = last 2 weeks, 2 = last 3 weeks, 3 = last 4 weeks) rather than an
   * isolated, non-overlapping week further in the past.
   */
  calculateWeekBoundary(weeksAgo: number): {
    start: string;
    end: string;
    startIso: string;
    endIso: string;
    startDateObj: Date;
    endDateObj: Date;
  } {
    const now = new Date();
    const end = new Date(
      Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate(),
        23,
        59,
        59,
        999
      )
    );
    const start = new Date(
      Date.UTC(
        end.getUTCFullYear(),
        end.getUTCMonth(),
        end.getUTCDate() - (weeksAgo + 1) * 7 + 1,
        0,
        0,
        0,
        0
      )
    );
    return {
      start: start.toISOString().slice(0, 10),
      end: end.toISOString().slice(0, 10),
      startIso: start.toISOString(),
      endIso: end.toISOString(),
      startDateObj: new Date(
        start.getUTCFullYear(),
        start.getUTCMonth(),
        start.getUTCDate()
      ),
      endDateObj: new Date(
        end.getUTCFullYear(),
        end.getUTCMonth(),
        end.getUTCDate()
      ),
    };
  }

  count(kind: 'pii' | 'riskyAdvice' | 'smoke'): number {
    return this.findings.filter((item) =>
      kind === 'smoke' ? item.smokeFireMentionCount > 0 : item[kind]
    ).length;
  }

  messageHistory(finding: ComplianceFinding): unknown[] {
    const history = finding.messageHistory;
    if (Array.isArray(history)) return history;
    if (typeof history === 'string') {
      try {
        const parsed: unknown = JSON.parse(history);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  }

  messageRole(message: unknown): string {
    return this.messageField(message, 'role') || 'Conversation message';
  }

  messageText(message: unknown): string {
    const content =
      this.messageValue(message, 'content') ??
      this.messageValue(message, 'message');
    if (typeof content === 'string') return content;
    if (Array.isArray(content)) {
      return content
        .map((block) => this.contentText(block))
        .filter(Boolean)
        .join('\n');
    }
    return this.contentText(content);
  }

  messageTimestamp(message: unknown): string {
    return this.messageField(message, 'timestamp');
  }

  private contentText(content: unknown): string {
    if (typeof content === 'string') return content;
    if (!content || typeof content !== 'object') return '';
    const record = content as Record<string, unknown>;
    return typeof record['text'] === 'string'
      ? record['text']
      : typeof record['message'] === 'string'
        ? record['message']
        : '';
  }

  private messageField(message: unknown, field: string): string {
    const value = this.messageValue(message, field);
    return typeof value === 'string' ? value : '';
  }

  private messageValue(message: unknown, field: string): unknown {
    return message && typeof message === 'object'
      ? (message as Record<string, unknown>)[field]
      : undefined;
  }
}
